import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, CardActionArea, Grid, Button, Chip, Menu, MenuItem, ListItemIcon, Divider, Avatar, Dialog, DialogContent } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShieldIcon from '@mui/icons-material/Shield';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CropRotateIcon from '@mui/icons-material/CropRotate';
import { useAuth } from '../contexts/AuthContext';
import { getManualLabel, openUserManual } from '../utils/userManual';
import DrawingHistoryDialog from './DrawingHistoryDialog';
import ProfileDialog from './ProfileDialog';
import AdminPanel from './AdminPanel';

/**
 * Стартовый экран выбора режима работы
 */
const PRODUCT_NEWS_ID = 'dwg-step-3d-views-v4';

const PRODUCT_NEWS = [
  {
    icon: <StraightenIcon sx={{ fontSize: 22, color: '#6a1b9a' }} />,
    title: 'Чертёж в DWG.',
    desc: 'В меню «Сохранить как» появился формат DWG. Файл открывается в AutoCAD и других CAD-программах.',
  },
  {
    icon: <ViewInArIcon sx={{ fontSize: 22, color: '#e65100' }} />,
    title: 'Модель в STEP.',
    desc: 'Там же можно сохранить всю сборку в формате STEP и открыть её в любой 3D-программе.',
  },
  {
    icon: <ViewInArIcon sx={{ fontSize: 22, color: '#0277bd' }} />,
    title: 'Просмотр в 3D.',
    desc: 'Кнопка справа открывает модель прямо из чертежа. Её можно покрутить и рассмотреть со всех сторон.',
  },
  {
    icon: <CropRotateIcon sx={{ fontSize: 22, color: '#2e7d32' }} />,
    title: 'Новые виды на листе.',
    desc: 'Кнопка «Добавить вид» добавляет на лист вид сверху, снизу, слева или справа.',
  },
];

function productNewsSeenKey(uid) {
  return `cad_news_seen_${PRODUCT_NEWS_ID}_${uid}`;
}

function StartScreen({ onSelectMode, onOpenDrawingFromHistory }) {
  const { currentUser, isAdmin, isSuperAdmin, logout } = useAuth();
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const seen = localStorage.getItem(productNewsSeenKey(currentUser.uid)) === 'true';
    setNewsOpen(!seen);
  }, [currentUser?.uid]);

  const handleCloseNews = () => {
    if (currentUser?.uid) {
      localStorage.setItem(productNewsSeenKey(currentUser.uid), 'true');
    }
    setNewsOpen(false);
  };

  const emailInitial = currentUser?.email ? currentUser.email[0].toUpperCase() : '?';
  const manualLabel = getManualLabel(isAdmin);

  const handleOpenManual = () => {
    setAccountMenuAnchor(null);
    openUserManual(isAdmin);
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 50%, #fafafa 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Декоративные элементы фона с красными акцентами */}
      <Box
        sx={{
          position: 'absolute',
          top: '-6.25rem', // -100px
          right: '-6.25rem',
          width: '25rem', // 400px
          height: '25rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(211,47,47,0.05) 0%, rgba(211,47,47,0.02) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-9.375rem', // -150px
          left: '-9.375rem',
          width: '31.25rem', // 500px
          height: '31.25rem',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(211,47,47,0.03) 0%, rgba(211,47,47,0.01) 100%)',
        }}
      />
      {/* Декоративные линии */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '0.25rem', // 4px
          background: 'linear-gradient(90deg, #d32f2f 0%, #e53935 50%, #d32f2f 100%)',
        }}
      />

      {/* Панель пользователя — правый верхний угол */}
      <Box sx={{ position: 'absolute', top: 16, right: 24, display: 'flex', alignItems: 'center', gap: 1, zIndex: 10 }}>
        {isAdmin && (
          <Chip
            icon={<AdminPanelSettingsIcon />}
            label={isSuperAdmin ? 'Администратор' : 'Инженер'}
            size="small"
            sx={{ backgroundColor: 'rgba(211,47,47,0.1)', color: '#d32f2f', fontWeight: 600, border: '1px solid rgba(211,47,47,0.2)' }}
          />
        )}
        <Button
          onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: '#9e9e9e', transition: 'transform 0.2s', transform: accountMenuAnchor ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 0.75,
            borderRadius: '0.5rem',
            border: '0.0625rem solid #e0e0e0',
            background: '#fff',
            textTransform: 'none',
            '&:hover': { background: '#f5f5f5', borderColor: '#bdbdbd' },
          }}
        >
          <Avatar sx={{ width: '1.75rem', height: '1.75rem', fontSize: '0.8rem', fontWeight: 700, background: 'linear-gradient(135deg, #d32f2f, #e53935)' }}>
            {emailInitial}
          </Avatar>
          <Typography sx={{ fontSize: '0.8rem', color: '#424242', maxWidth: '11rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentUser?.email}
          </Typography>
        </Button>

        <Menu
          anchorEl={accountMenuAnchor}
          open={Boolean(accountMenuAnchor)}
          onClose={() => setAccountMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 0.75,
              minWidth: '14rem',
              borderRadius: '0.625rem',
              border: '0.0625rem solid #e8e8e8',
              boxShadow: '0 0.5rem 1.5rem rgba(0,0,0,0.12)',
              overflow: 'hidden',
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '0.0625rem solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', mb: 0.25 }}>Вы вошли как</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.email}
            </Typography>
          </Box>

          <MenuItem onClick={() => { setAccountMenuAnchor(null); setProfileOpen(true); }} sx={{ py: 1.25, gap: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 'unset' }}><PersonIcon fontSize="small" sx={{ color: '#616161' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.85rem' }}>Профиль</Typography>
          </MenuItem>

          <MenuItem onClick={() => { setAccountMenuAnchor(null); setHistoryOpen(true); }} sx={{ py: 1.25, gap: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 'unset' }}><HistoryIcon fontSize="small" sx={{ color: '#616161' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.85rem' }}>История чертежей</Typography>
          </MenuItem>

          <MenuItem onClick={handleOpenManual} sx={{ py: 1.25, gap: 1.5 }}>
            <ListItemIcon sx={{ minWidth: 'unset' }}><MenuBookIcon fontSize="small" sx={{ color: '#616161' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.85rem' }}>{manualLabel}</Typography>
          </MenuItem>

          {isSuperAdmin && (
            <MenuItem onClick={() => { setAccountMenuAnchor(null); setAdminOpen(true); }} sx={{ py: 1.25, gap: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 'unset' }}><ShieldIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
              <Typography sx={{ fontSize: '0.85rem', color: '#d32f2f', fontWeight: 500 }}>Администрирование</Typography>
            </MenuItem>
          )}

          <Divider />

          <MenuItem
            onClick={() => { setAccountMenuAnchor(null); logout(); }}
            sx={{ py: 1.25, gap: 1.5, '&:hover': { background: 'rgba(211,47,47,0.06)' } }}
          >
            <ListItemIcon sx={{ minWidth: 'unset' }}><LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.85rem', color: '#d32f2f', fontWeight: 500 }}>Выйти</Typography>
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ width: '100%', px: 3, display: 'flex', justifyContent: 'center' }}>
        <Grid container spacing={4} sx={{ maxWidth: '56.25rem' }}> {/* 900px */}
          {/* Заголовок с логотипом */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                mb: 1,
              }}
            >
              <Box
                component="img"
                src="/proxytherm-logo.png"
                alt="ProxyTherm Logo"
                onError={(e) => { e.target.style.display = 'none'; }} // Скрываем если не загрузился
                sx={{
                  height: '5rem', // 80px
                  width: 'auto',
                  opacity: 0.9,
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  color: '#212121',
                  fontWeight: 700,
                  textShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.1)', // 0 2px 4px
                }}
              >
                ProxythermCAD
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: '#757575',
                textAlign: 'center',
                fontWeight: 400,
              }}
            >
              Система проектирования оборудования для обвязки котельной
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => openUserManual(isAdmin)}
              sx={{
                display: 'block',
                width: '100%',
                mt: 1.5,
                textAlign: 'center',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#d32f2f',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': { textDecoration: 'underline', color: '#b71c1c' },
              }}
            >
              Первый раз? Откройте {manualLabel.toLowerCase()}
            </Typography>
          </Grid>

          {/* Ручной конструктор */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card
              sx={{
                flex: 1,
                display: 'flex',
                background: '#ffffff',
                borderRadius: 4,
                border: '0.125rem solid #e0e0e0',
                boxShadow: '0 0.25rem 0.75rem rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-0.5rem)',
                  boxShadow: '0 1.25rem 2.5rem rgba(211,47,47,0.15)',
                  border: '0.125rem solid #d32f2f',
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectMode('manual')}
                sx={{ height: '100%', p: 3, display: 'flex', alignItems: 'stretch' }}
              >
                <CardContent sx={{ textAlign: 'center', width: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                  <Box
                    sx={{
                      width: '5rem', // 80px
                      height: '5rem',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(211,47,47,0.1) 0%, rgba(198,40,40,0.15) 100%)',
                      border: '0.1875rem solid #d32f2f', // 3px
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.25rem', // 0 auto 20px
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <BuildIcon sx={{ fontSize: '2.5rem', color: '#d32f2f' }} /> {/* 40px */}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5, color: '#212121', minHeight: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Ручной конструктор
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.45, fontSize: '0.95rem' }}>
                    Создание чертежа вручную с полным контролем над каждым элементом.
                    Размещайте трубы, фитинги, добавляйте размеры и аннотации.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Автоматический конструктор */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card
              sx={{
                flex: 1,
                display: 'flex',
                background: '#ffffff',
                borderRadius: 4,
                border: '0.125rem solid #e0e0e0',
                boxShadow: '0 0.25rem 0.75rem rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-0.5rem)',
                  boxShadow: '0 1.25rem 2.5rem rgba(229,57,53,0.15)',
                  border: '0.125rem solid #e53935',
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectMode('auto')}
                sx={{ height: '100%', p: 3, display: 'flex', alignItems: 'stretch' }}
              >
                <CardContent sx={{ textAlign: 'center', width: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                  <Box
                    sx={{
                      width: '5rem', // 80px
                      height: '5rem',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(229,57,53,0.1) 0%, rgba(211,47,47,0.15) 100%)',
                      border: '0.1875rem solid #e53935', // 3px
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.25rem', // 0 auto 20px
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <AutoFixHighIcon sx={{ fontSize: '2.5rem', color: '#e53935' }} /> {/* 40px */}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5, color: '#212121', minHeight: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Автоматический конструктор
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.45, fontSize: '0.95rem' }}>
                    Генерация чертежа по параметрам. Выберите тип изделия,
                    укажите характеристики — чертёж создастся автоматически.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={newsOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 560,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: '12px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
            backgroundImage: 'linear-gradient(#d32f2f, #d32f2f)',
            backgroundSize: '100% 3px',
            backgroundRepeat: 'no-repeat',
          },
        }}
      >
        <DialogContent sx={{ px: '22px', pt: '20px', pb: '14px' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#212121', lineHeight: 1.3 }}>
            Что нового в чертежах
          </Typography>
          <Typography sx={{ fontSize: 16, color: '#616161', lineHeight: 1.45, mt: '8px', mb: '16px' }}>
            Чертёж теперь можно сохранить в DWG, модель — в STEP и посмотреть в 3D, а на лист добавить недостающие виды.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {PRODUCT_NEWS.map((item) => (
              <Box key={item.title} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '10px', px: '14px', py: '12px' }}>
                <Box sx={{ flexShrink: 0, mt: '2px' }}>{item.icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#212121', lineHeight: 1.35 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: 15, color: '#757575', lineHeight: 1.45, mt: '2px' }}>{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '8px' }}>
            <Button
              onClick={handleCloseNews}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: '#d32f2f', minWidth: 0, px: '8px' }}
            >
              Понятно
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} onOpenDrawing={onOpenDrawingFromHistory} />

      <DrawingHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        uid={currentUser?.uid}
        onOpenDrawing={(data) => {
          setHistoryOpen(false);
          if (onOpenDrawingFromHistory) onOpenDrawingFromHistory(data);
        }}
      />

      {/* Информация о разработчике */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        right: 20,
        textAlign: 'right',
      }}>
        <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.72rem', display: 'block', lineHeight: 1.6 }}>
          Разработчик: Базуев Павел Сергеевич
        </Typography>
        <Typography
          component="a"
          href="mailto:iceeydev78@gmail.com"
          variant="caption"
          sx={{ color: '#bbb', fontSize: '0.72rem', textDecoration: 'none', '&:hover': { color: '#d32f2f' } }}
        >
          iceeydev78@gmail.com
        </Typography>
      </Box>
    </Box>
  );
}

export default StartScreen;

